import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Sparkles, XCircle, Clock, Search, Ban, RotateCcw, Trash2, LogIn, ChevronRight } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Navigate, Link } from "react-router-dom";

// Admin dashboard: list every user and their subscription state, with quick
// actions to grant/revoke a Free (no monthly fee) plan or extend a trial by
// a chosen number of days.

const STATUS_STYLES = {
  trialing: "bg-[#EEF3EC] text-[#3B5B3E] border-[#CFDDCB]",
  active:   "bg-[#EEF3EC] text-[#3B5B3E] border-[#CFDDCB]",
  free:     "bg-[#E9F0F5] text-[#1F4C6B] border-[#C9D9E5]",
  past_due: "bg-[#F7EFEC] text-[#8C3A30] border-[#E4C9C2]",
  canceled: "bg-[#F2EFEB] text-[#59605C] border-[#EBE6E0]",
  expired:  "bg-[#F7EFEC] text-[#8C3A30] border-[#E4C9C2]",
  none:     "bg-[#F2EFEB] text-[#8A918D] border-[#EBE6E0]",
};

const STATUS_LABEL = {
  trialing: "Trial",
  active: "Active",
  free: "Free",
  past_due: "Past due",
  canceled: "Canceled",
  expired: "Expired",
  none: "None",
};

const FILTERS = ["all", "trialing", "active", "free", "expired"];

export default function AdminUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [query, setQuery] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const [users, funnelRes] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/funnel").catch(() => ({ data: null })),
      ]);
      setItems(users.data.items || []);
      setFunnel(funnelRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: items.length };
    FILTERS.slice(1).forEach((f) => { c[f] = items.filter((i) => i.subscription_status === f).length; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((u) => {
      if (filter !== "all" && u.subscription_status !== filter) return false;
      if (!q) return true;
      return (u.email || "").toLowerCase().includes(q) || (u.full_name || "").toLowerCase().includes(q);
    });
  }, [items, filter, query]);

  if (user && user.role !== "admin") {
    return <Navigate to="/app/dashboard" replace />;
  }

  const grantFree = async (u) => {
    if (!window.confirm(`Grant a FREE (no monthly fees) account to ${u.email}?`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/grant-free`);
      toast.success(`${u.email} is now Free`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const revokeFree = async (u) => {
    if (!window.confirm(`Revoke Free status from ${u.email}?`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/revoke-free`);
      toast.success(`Free status removed for ${u.email}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const extendTrial = async (u) => {
    const raw = window.prompt(`Extend ${u.email}'s trial by how many days?`, "30");
    if (raw === null) return;
    const days = parseInt(raw, 10);
    if (Number.isNaN(days) || days <= 0 || days > 365) {
      toast.error("Enter a number between 1 and 365");
      return;
    }
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/extend-trial`, { days });
      toast.success(`Trial extended by ${days} days for ${u.email}`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const suspendUser = async (u) => {
    if (!window.confirm(`Suspend ${u.email}? They will not be able to log in until you unsuspend.`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/suspend`);
      toast.success(`${u.email} suspended`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const unsuspendUser = async (u) => {
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/unsuspend`);
      toast.success(`${u.email} can log in again`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const deleteUser = async (u) => {
    const first = window.confirm(
      `⚠️  DELETE ${u.email}?\n\nThis IRREVERSIBLY wipes their account and every bill, income, snapshot, and pay period they own. Use only for test accounts or spam.\n\nClick OK to continue.`
    );
    if (!first) return;
    const confirmText = window.prompt(
      `Type DELETE (all caps) to confirm hard delete of ${u.email}:`, ""
    );
    if (confirmText !== "DELETE") {
      if (confirmText !== null) toast.error("Confirmation text didn't match. Nothing was deleted.");
      return;
    }
    setBusyId(u.id);
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${u.email} (${Object.values(data.deleted || {}).reduce((s, n) => s + n, 0)} rows)`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(
      `Log in as ${u.email}?\n\nYour admin session will be replaced with their session. You'll need to log back in to your admin account when you're done.`
    )) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/impersonate`);
      toast.success(`Now logged in as ${u.email}`);
      // Full reload to pick up the new auth cookie + user context.
      window.location.href = "/app";
    } catch (err) { toast.error(formatApiError(err)); setBusyId(null); }
  };

  const relativeTime = (iso) => {
    if (!iso) return "Never";
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "1d ago";
    if (days < 30) return `${days}d ago`;
    if (days < 365) return `${Math.floor(days / 30)}mo ago`;
    return `${Math.floor(days / 365)}y ago`;
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#8A918D] mb-1">Admin</div>
          <h1 className="font-heading font-bold text-3xl text-[#2C302E]">Users</h1>
          <p className="text-sm text-[#59605C] mt-1">
            Grant Free accounts, extend trials, and see who&apos;s using the app.
          </p>
        </div>
        <button onClick={load} className="btn-secondary text-sm" data-testid="admin-users-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Activation funnel — snapshot of how many users make it through
          each step. Cheap read from /admin/funnel; refreshed on every
          Refresh click. Hidden while loading so it doesn't flash empty. */}
      {funnel && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="admin-funnel">
          {[
            { key: "signed_up", label: "Signed up" },
            { key: "onboarded", label: "Onboarded" },
            { key: "has_bills", label: "Has bills" },
            { key: "has_income", label: "Has income" },
            { key: "reconciled_once", label: "Reconciled" },
            { key: "has_snapshot", label: "Shared snap" },
            { key: "active_7d", label: "Active 7d" },
          ].map(({ key, label }) => (
            <div
              key={key}
              className="rounded-2xl border border-[#D8CFB9] bg-white p-3"
              data-testid={`admin-funnel-${key}`}
            >
              <div className="text-[10px] font-semibold uppercase tracking-widest text-[#8A918D]">{label}</div>
              <div className="font-heading text-2xl font-semibold text-[#2C302E] mt-0.5">{funnel[key] ?? 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2" data-testid="admin-users-filters">
          {FILTERS.map((k) => (
            <button
              key={k}
              data-testid={`admin-users-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                filter === k
                  ? "bg-[#EEF3EC] border-[#3B5B3E] text-[#3B5B3E]"
                  : "bg-white border-[#EBE6E0] text-[#59605C] hover:bg-[#F2EFEB]"
              }`}
            >
              {k === "all" ? "All" : STATUS_LABEL[k]} · {counts[k] ?? 0}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A918D]" />
          <input
            type="text"
            placeholder="Search by email or name…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-full border border-[#D8CFB9] bg-white text-sm text-[#2C302E] focus:outline-none focus:border-[#4A6148]"
            data-testid="admin-users-search"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A918D]" data-testid="admin-users-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-3xl border border-dashed border-[#EBE6E0] bg-white/60 p-10 text-center"
          data-testid="admin-users-empty"
        >
          <div className="font-heading font-semibold text-[#2C302E]">No users match this filter</div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-users-list">
          {filtered.map((u) => {
            const style = STATUS_STYLES[u.subscription_status] || STATUS_STYLES.none;
            const label = STATUS_LABEL[u.subscription_status] || u.subscription_status;
            const busy = busyId === u.id;
            const isFree = u.subscription_status === "free";
            return (
              <li
                key={u.id}
                className="rounded-2xl border border-[#D8CFB9] bg-white p-4"
                data-testid={`admin-user-item-${u.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${style}`}
                        data-testid={`admin-user-status-${u.id}`}
                      >
                        {label}
                      </span>
                      {u.role === "admin" && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border bg-[#F7F3EA] text-[#7A5F0A] border-[#E5DEC7]">
                          Admin
                        </span>
                      )}
                      {u.suspended && (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border bg-[#FBF3E0] text-[#8A6A1A] border-[#D8B98A]"
                          data-testid={`admin-user-suspended-badge-${u.id}`}
                        >
                          Suspended
                        </span>
                      )}
                      <span className="text-sm font-semibold text-[#2C302E]">
                        {u.full_name || u.email}
                      </span>
                      {u.full_name && (
                        <span className="text-xs text-[#59605C]">{u.email}</span>
                      )}
                    </div>
                    <div className="text-xs text-[#8A918D] flex flex-wrap gap-x-3 gap-y-1">
                      {u.created_at && <span>Signed up {formatDate(u.created_at)}</span>}
                      <span data-testid={`admin-user-last-seen-${u.id}`}>Last seen {relativeTime(u.last_login_at)}</span>
                      {u.trial_end && <span>Trial ends {formatDate(u.trial_end)}</span>}
                      {u.feedback_count > 0 && <span>{u.feedback_count} feedback</span>}
                      {!u.onboarding_completed && <span className="text-[#8C3A30]">Onboarding incomplete</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={`/app/admin/users/${u.id}`}
                      className="btn-ghost !px-3 !py-1.5 text-xs"
                      data-testid={`admin-user-view-${u.id}`}
                    >
                      <ChevronRight size={14} /> View
                    </Link>
                    {u.role !== "admin" && !u.suspended && (
                      <button
                        onClick={() => impersonate(u)}
                        disabled={busy}
                        className="btn-ghost !px-3 !py-1.5 text-xs text-[#4A6148]"
                        data-testid={`admin-user-impersonate-${u.id}`}
                        title="Log in as this user (your admin session will be replaced)"
                      >
                        <LogIn size={14} /> Login as
                      </button>
                    )}
                    {isFree ? (
                      <button
                        onClick={() => revokeFree(u)}
                        disabled={busy}
                        className="btn-ghost !px-3 !py-1.5 text-xs text-[#8C3A30]"
                        data-testid={`admin-user-revoke-free-${u.id}`}
                      >
                        <XCircle size={14} /> Revoke Free
                      </button>
                    ) : (
                      <button
                        onClick={() => grantFree(u)}
                        disabled={busy}
                        className="btn-primary !px-3 !py-1.5 text-xs"
                        data-testid={`admin-user-grant-free-${u.id}`}
                      >
                        <Sparkles size={14} /> Grant Free
                      </button>
                    )}
                    <button
                      onClick={() => extendTrial(u)}
                      disabled={busy}
                      className="btn-ghost !px-3 !py-1.5 text-xs"
                      data-testid={`admin-user-extend-trial-${u.id}`}
                    >
                      <Clock size={14} /> Extend trial
                    </button>
                    {/* Suspend / unsuspend — soft, reversible block on login. */}
                    {u.suspended ? (
                      <button
                        onClick={() => unsuspendUser(u)}
                        disabled={busy}
                        className="btn-ghost !px-3 !py-1.5 text-xs text-[#3B5B3E]"
                        data-testid={`admin-user-unsuspend-${u.id}`}
                      >
                        <RotateCcw size={14} /> Unsuspend
                      </button>
                    ) : (
                      <button
                        onClick={() => suspendUser(u)}
                        disabled={busy || u.role === "admin"}
                        className="btn-ghost !px-3 !py-1.5 text-xs text-[#8A6A1A]"
                        data-testid={`admin-user-suspend-${u.id}`}
                        title={u.role === "admin" ? "Can't suspend an admin" : "Block login without deleting data"}
                      >
                        <Ban size={14} /> Suspend
                      </button>
                    )}
                    {/* Hard delete — IRREVERSIBLE. Guarded with a double
                        confirm (window.confirm + typed 'DELETE'). */}
                    <button
                      onClick={() => deleteUser(u)}
                      disabled={busy || u.role === "admin"}
                      className="btn-ghost !px-3 !py-1.5 text-xs text-[#8C3A30]"
                      data-testid={`admin-user-delete-${u.id}`}
                      title={u.role === "admin" ? "Can't delete an admin" : "Hard-delete this user and all their data"}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
